import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";

const works = [
  {
    title: "AFM Properties",
    category: "Brand Identity, Motion Design",
    video: "/assets/work-afm-properties.mp4",
    glowA: "#667eea",
    glowB: "#764ba2",
  },
  {
    title: "Miruna",
    category: "SEO, Performance Marketing",
    image: "/assets/work-miruna.jpg",
    glowA: "#ff6b6b",
    glowB: "#ee5a24",
  },
  {
    title: "Route 75",
    category: "Brand Identity, Content Creation",
    video: "/assets/work-route75.mp4",
    glowA: "#f7971e",
    glowB: "#ffd200",
  },
  {
    title: "FitVantage",
    category: "Brand Identity, Brand Strategy, App Development",
    image: "/assets/work-fitvantage.jpg",
    glowA: "#56ab2f",
    glowB: "#a8e063",
  },
  {
    title: "Blingwood",
    category: "Brand Identity, Web Development",
    image: "/assets/work-blingwood.jpg",
    glowA: "#e74c3c",
    glowB: "#c0392b",
  },
  {
    title: "FunkySouq",
    category: "Content Creation, SEO, Performance Marketing",
    video: "/assets/work-funkysouq.mp4",
    glowA: "#667eea",
    glowB: "#43e97b",
  },
];

export default function WorksSection() {
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(null);

  const goNext = useCallback(() => {
    setActive((prev) => (prev + 1) % works.length);
  }, []);

  const goPrev = useCallback(() => {
    setActive((prev) => (prev - 1 + works.length) % works.length);
  }, []);

  // Auto-rotation
  useEffect(() => {
    const interval = setInterval(() => {
      goNext();
    }, 5000); // Auto-advance every 5 seconds
    return () => clearInterval(interval);
  }, [goNext]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev]);

  const getCardStyle = useCallback((index) => {
    const diff = index - active;
    const isActive = index === active;

    let translateX = diff * 340;
    let scale = 0.85;
    let opacity = 0.5;
    let zIndex = 1;

    if (isActive) {
      scale = 1.12;
      opacity = 1;
      zIndex = 10;
    } else if (Math.abs(diff) === 1 || (diff === works.length - 1) || (diff === -(works.length - 1))) {
      scale = 0.9;
      opacity = 0.6;
      zIndex = 5;
    }

    return { translateX, scale, opacity, zIndex };
  }, [active]);

  // Calculate visible cards (show active + neighbors)
  const getVisibleCards = useCallback(() => {
    const visible = [];
    for (let offset = -2; offset <= 2; offset++) {
      const idx = (active + offset + works.length) % works.length;
      visible.push({ index: idx, offset });
    }
    return visible;
  }, [active]);

  return (
    <section id="works" data-testid="works-section" className="py-16 md:py-20 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #000 0%, #0a0e27 50%, #000 100%)" }}>
      {/* Ambient glow from active card */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full blur-[200px] opacity-20 transition-all duration-1000 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${works[active].glowA}, ${works[active].glowB}, transparent)` }}
      />

      <div className="container mx-auto px-6 md:px-12 max-w-[1400px] relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 md:mb-20"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">
            Portfolio
          </p>
          <h2 className="font-syne text-4xl md:text-6xl font-bold tracking-tight text-white">
            Selected <span className="gradient-text">Work</span>
          </h2>
        </motion.div>

        {/* Carousel */}
        <div className="relative h-[480px] md:h-[560px] flex items-center justify-center">
          {getVisibleCards().map(({ index, offset }) => {
            const work = works[index];
            const isActive = index === active;
            const isHovered = hovered === index;
            const { scale, opacity, zIndex } = getCardStyle(index);

            const translateX = offset * 320;

            return (
              <motion.div
                key={`${work.title}-${index}`}
                data-testid={`work-card-${work.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="absolute cursor-pointer"
                style={{ zIndex }}
                animate={{
                  x: translateX,
                  scale: isHovered && !isActive ? 0.95 : scale,
                  opacity: isHovered && !isActive ? 0.85 : opacity,
                }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setActive(index)}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
              >
                <div
                  className="relative w-[280px] h-[380px] md:w-[300px] md:h-[420px] overflow-hidden"
                  style={{
                    borderRadius: "20px",
                    boxShadow: isActive
                      ? `0 0 60px -10px ${work.glowA}80, 0 0 100px -20px ${work.glowB}40`
                      : `0 8px 32px rgba(0,0,0,0.4)`,
                    border: isActive
                      ? `2px solid ${work.glowA}60`
                      : "2px solid rgba(255,255,255,0.05)",
                    transition: "box-shadow 0.6s ease, border 0.6s ease",
                  }}
                >
                  {/* Media */}
                  {work.video ? (
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload={isActive || Math.abs(offset) <= 1 ? "auto" : "none"}
                      className="absolute inset-0 w-full h-full object-cover"
                      width="300"
                      height="420"
                    >
                      <source src={work.video} type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      src={work.image}
                      alt={work.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      width="300"
                      height="420"
                    />
                  )}

                  {/* Dim overlay for non-active */}
                  {!isActive && (
                    <div className="absolute inset-0 bg-black/30 transition-opacity duration-500" />
                  )}

                  {/* Frosted glass info panel */}
                  <div
                    className="absolute bottom-0 left-0 right-0 p-5 transition-all duration-500"
                    style={{
                      background: "rgba(0, 0, 0, 0.45)",
                      backdropFilter: "blur(16px)",
                      WebkitBackdropFilter: "blur(16px)",
                      borderTop: "1px solid rgba(255,255,255,0.08)",
                      opacity: isActive || isHovered ? 1 : 0.7,
                      transform: isActive || isHovered ? "translateY(0)" : "translateY(4px)",
                    }}
                  >
                    <h3 className="font-syne text-lg md:text-xl font-bold text-white leading-tight mb-1">
                      {work.title}
                    </h3>
                    <p className="text-xs md:text-sm text-zinc-300/70 italic font-light tracking-wide">
                      {work.category}
                    </p>
                  </div>

                  {/* Active glow border overlay */}
                  {isActive && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        borderRadius: "20px",
                        background: `linear-gradient(135deg, ${work.glowA}10, transparent 50%, ${work.glowB}10)`,
                      }}
                    />
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>


        {/* Active card title display */}
        <div className="text-center mt-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600">
            {String(active + 1).padStart(2, "0")} / {String(works.length).padStart(2, "0")}
          </p>
        </div>
      </div>
    </section>
  );
}
